// Reads the stored device_settings into what the settings dialog shows. The stored copy can arrive as an object, as a JSON string,
// with a time as "HH:mm", "HH:mm:ss" (the tenant/admin endpoints used to default to "04:00:00") or null/"" (an older save with no value):
// every one of those must show the real time, and a missing value must show the default, never an empty box.

export const TIME_DEFAULTS = { dailyRestartTime: "04:00", operatingStartTime: "09:00", operatingSleepTime: "15:05" };
export const TIME_KEYS = Object.keys(TIME_DEFAULTS);

// "4:00", "04:00", "04:00:00" -> "04:00"; anything else -> null
export const toHHmm = (v) => {
    if (typeof v !== "string") return null;
    const m = /^\s*([01]?\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?\s*$/.exec(v);
    return m ? `${m[1].padStart(2, "0")}:${m[2]}` : null;
};

export const parseStored = (raw) => {
    if (typeof raw === "string") {
        try { raw = JSON.parse(raw); } catch (e) { return {}; }
    }
    return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
};

// stored object -> the same object with every time key a valid "HH:mm" (default when absent/null/unreadable); other keys untouched, null never overrides a default
export const readDeviceSettings = (raw) => {
    const stored = parseStored(raw);
    const out = {};
    Object.keys(stored).forEach((k) => { if (stored[k] !== null && stored[k] !== undefined) out[k] = stored[k]; });
    TIME_KEYS.forEach((k) => { out[k] = toHHmm(stored[k]) ?? TIME_DEFAULTS[k]; });
    // operating hours are OFF unless a stored value says otherwise (a hours-ON default shut PROD screens down after 15:05)
    out.operatingHoursEnabled = stored.operatingHoursEnabled === true || stored.operatingHoursEnabled === "true" || stored.operatingHoursEnabled === 1;
    return out;
};

// 12-hour display for the summary sentence: parts only, the caller picks the words and the digit style
export const to12h = (hhmm) => {
    const t = toHHmm(hhmm);
    if (!t) return null;
    const [h, m] = t.split(":").map(Number);
    return { hour: h % 12 === 0 ? 12 : h % 12, minute: m, pm: h >= 12 };
};
export const endsNextDay = (start, end) => {
    const a = toHHmm(start), b = toHHmm(end);
    return !!a && !!b && b < a;
};

const AR_DIGITS = "٠١٢٣٤٥٦٧٨٩";
export const localiseDigits = (str, lang) => (lang === "ar" ? String(str).replace(/\d/g, (d) => AR_DIGITS[d]) : String(str));

// "8:00 AM" / "٨:٠٠ ص": 12-hour clock with the page language's am/pm words and the platform's digit style (Arabic-Indic in Arabic, Latin in English)
export const format12h = (hhmm, lang, am, pm) => {
    const p = to12h(hhmm);
    if (!p) return null;
    return localiseDigits(`${p.hour}:${String(p.minute).padStart(2, "0")}`, lang) + " " + (p.pm ? pm : am);
};

// the time that was REALLY saved (HH:mm), or null: the placeholders above are for the boxes only and are never posted
export const storedTime = (raw, key) => toHHmm(parseStored(raw)[key]);

// the device card's hours text parts: null while the hours are off / nothing is stored
export const hoursWindow = (raw) => {
    const s = readDeviceSettings(raw);
    if (!s.operatingHoursEnabled) return null;
    return { from: storedTime(raw, "operatingStartTime") || s.operatingStartTime, to: storedTime(raw, "operatingSleepTime") || s.operatingSleepTime };
};
